/**
 * Web i18n: the shared core (@prop/i18n) plus a React context for the current language.
 * A public page's language is in its URL (/ru/…, /es/…, English at the root; ADR-0062, ADR-0065); the app paths use the language
 * remembered per browser (localStorage). It is sent to the API as `X-Lang`, so API error texts arrive translated.
 */
import { DEFAULT_LANG, defineNamespace, LANG_META, localeTagFor, type Lang, parseLang } from "@prop/i18n";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { navigate, usePathname } from "wouter/use-browser-location";
import { localePath, splitLocale } from "../app/publicRoutes";
import { setApiLang } from "../lib/api";
import enAdmin from "./en/admin";
import enBreach from "./en/breach";
import enAuth from "./en/auth";
import enSecurity from "./en/security";
import enCoach from "./en/coach";
import enConfig from "./en/config";
import enLanding from "./en/landing";
import enLegal from "./en/legal";
import enMoney from "./en/money";
import enMultiAccount from "./en/multiaccount";
import enRisk from "./en/risk";
import enShell from "./en/shell";
import enSupport from "./en/support";
import enTrade from "./en/trade";
import ruAdmin from "./ru/admin";
import ruBreach from "./ru/breach";
import ruAuth from "./ru/auth";
import ruSecurity from "./ru/security";
import ruCoach from "./ru/coach";
import ruConfig from "./ru/config";
import ruLanding from "./ru/landing";
import ruLegal from "./ru/legal";
import ruMoney from "./ru/money";
import ruMultiAccount from "./ru/multiaccount";
import ruRisk from "./ru/risk";
import ruShell from "./ru/shell";
import ruSupport from "./ru/support";
import ruTrade from "./ru/trade";
import esAdmin from "./es/admin";
import esBreach from "./es/breach";
import esAuth from "./es/auth";
import esSecurity from "./es/security";
import esCoach from "./es/coach";
import esConfig from "./es/config";
import esLanding from "./es/landing";
import esLegal from "./es/legal";
import esMoney from "./es/money";
import esMultiAccount from "./es/multiaccount";
import enWallet from "./en/wallet";
import ruWallet from "./ru/wallet";
import esWallet from "./es/wallet";
import esRisk from "./es/risk";
import esShell from "./es/shell";
import esSupport from "./es/support";
import esTrade from "./es/trade";

export const shellT = defineNamespace(enShell, { ru: ruShell, es: esShell });
export const authT = defineNamespace(enAuth, { ru: ruAuth, es: esAuth });
export const securityT = defineNamespace(enSecurity, { ru: ruSecurity, es: esSecurity });
export const adminT = defineNamespace(enAdmin, { ru: ruAdmin, es: esAdmin });
export const riskT = defineNamespace(enRisk, { ru: ruRisk, es: esRisk });
export const tradeT = defineNamespace(enTrade, { ru: ruTrade, es: esTrade });
export const moneyT = defineNamespace(enMoney, { ru: ruMoney, es: esMoney });
export const coachT = defineNamespace(enCoach, { ru: ruCoach, es: esCoach });
export const landingT = defineNamespace(enLanding, { ru: ruLanding, es: esLanding });
export const legalT = defineNamespace(enLegal, { ru: ruLegal, es: esLegal });
export const supportT = defineNamespace(enSupport, { ru: ruSupport, es: esSupport });
export const configT = defineNamespace(enConfig, { ru: ruConfig, es: esConfig });
export const breachT = defineNamespace(enBreach, { ru: ruBreach, es: esBreach });
export const multiAccountT = defineNamespace(enMultiAccount, { ru: ruMultiAccount, es: esMultiAccount });
export const walletT = defineNamespace(enWallet, { ru: ruWallet, es: esWallet });

const STORAGE_KEY = "prop.lang";

/** The language this browser chose (picked in the menu or the last public page opened), if any. */
function savedLang(): Lang | null {
  try {
    return parseLang(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null; // storage blocked
  }
}

const initialLang = (): Lang => savedLang() ?? parseLang(navigator.language) ?? DEFAULT_LANG;

// The language of the app paths, read once from storage (or the browser) and kept here.
let current: Lang | undefined;
const listeners = new Set<() => void>();
const snapshot = (): Lang => (current ??= initialLang());
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

function remember(l: Lang): void {
  if (current === l && savedLang() === l) return;
  current = l;
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch {
    // not persisted; the choice still applies to this tab
  }
  for (const fn of listeners) fn();
}

/** Set once the first page of this tab has been looked at: the redirect to the saved language happens on arrival only. */
let arrived = false;

type LangCtx = { lang: Lang; setLang: (l: Lang) => void };
const Ctx = createContext<LangCtx>({ lang: DEFAULT_LANG, setLang: () => undefined });

/**
 * The language of a page (ADR-0062). A public page has its own in its URL — /ru/faq is Russian, /faq English — so it
 * renders, prerendered and hydrated, in that language with no mismatch, and opening it remembers the language. Any other
 * path (the cabinet, sign-in, an unknown one) is in the remembered language. A visitor who chose Russian or Spanish and
 * arrives at an English URL is sent to the same page in their language once, after hydration; a crawler has no saved
 * choice and is never redirected. Picking a language on a public page opens that page in the language.
 */
export function LangProvider({ ssrPath, children }: { ssrPath?: string; children: ReactNode }) {
  const raw = usePathname({ ssrPath });
  const { lang: urlLang, path } = splitLocale(raw);
  const stored = useSyncExternalStore(subscribe, snapshot, () => DEFAULT_LANG);
  const lang = urlLang ?? stored;
  // <html lang> follows before the page renders: number formatting reads it (lib/fmt.ts). On a prerendered page it
  // already holds the page's language, so hydration changes nothing.
  if (typeof document !== "undefined" && document.documentElement.lang !== LANG_META[lang].tag) document.documentElement.lang = LANG_META[lang].tag;
  useEffect(() => setApiLang(lang), [lang]);
  useEffect(() => {
    const first = !arrived;
    arrived = true;
    if (!urlLang) return;
    // A first visit without a saved choice follows the browser's language (a Russian browser on / opens /ru); crawlers
    // announce English and stay. Once a language is remembered it wins.
    const saved = savedLang() ?? (first ? parseLang(navigator.language) : null);
    if (first && urlLang === DEFAULT_LANG && saved && saved !== urlLang) {
      navigate(localePath(path, saved) + location.search + location.hash, { replace: true });
      return;
    }
    remember(urlLang);
  }, [urlLang, path]);
  const value = useMemo(
    () => ({
      lang,
      setLang: (l: Lang) => {
        remember(l);
        if (urlLang && l !== urlLang) navigate(localePath(path, l) + location.search + location.hash);
      },
    }),
    [lang, urlLang, path],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangCtx {
  return useContext(Ctx);
}

export function useShellT() {
  const { lang } = useLang();
  return useMemo(() => shellT.tFor(lang), [lang]);
}
export function useAuthT() {
  const { lang } = useLang();
  return useMemo(() => authT.tFor(lang), [lang]);
}
export function useSecurityT() {
  const { lang } = useLang();
  return useMemo(() => securityT.tFor(lang), [lang]);
}
export function useAdminT() {
  const { lang } = useLang();
  return useMemo(() => adminT.tFor(lang), [lang]);
}
export function useLandingT() {
  const { lang } = useLang();
  return useMemo(() => landingT.tFor(lang), [lang]);
}
export function useLegalT() {
  const { lang } = useLang();
  return useMemo(() => legalT.tFor(lang), [lang]);
}
export function useCoachT() {
  const { lang } = useLang();
  return useMemo(() => coachT.tFor(lang), [lang]);
}
export function useMoneyT() {
  const { lang } = useLang();
  return useMemo(() => moneyT.tFor(lang), [lang]);
}
export function useTradeT() {
  const { lang } = useLang();
  return useMemo(() => tradeT.tFor(lang), [lang]);
}
export function useBreachT() {
  const { lang } = useLang();
  return useMemo(() => breachT.tFor(lang), [lang]);
}
export function useSupportT() {
  const { lang } = useLang();
  return useMemo(() => supportT.tFor(lang), [lang]);
}
export function useConfigT() {
  const { lang } = useLang();
  return useMemo(() => configT.tFor(lang), [lang]);
}
export function useRiskT() {
  const { lang } = useLang();
  return useMemo(() => riskT.tFor(lang), [lang]);
}
export function useMultiAccountT() {
  const { lang } = useLang();
  return useMemo(() => multiAccountT.tFor(lang), [lang]);
}
/** ADR-0050: the balance, top-ups, paying fees from the balance or in crypto. */
export function useWalletT() {
  const { lang } = useLang();
  return useMemo(() => walletT.tFor(lang), [lang]);
}

/** Date + time in the UI language (the locale comes from the language, never a literal tag). */
export function useFormatDateTime() {
  const { lang } = useLang();
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat(localeTagFor(lang), { dateStyle: "medium", timeStyle: "short" });
    return (iso: string | null | undefined) => (iso ? fmt.format(new Date(iso)) : "—");
  }, [lang]);
}
